import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/utils'

/* Field — the client's signature input, and the one component most likely to
   be rebuilt wrongly.

   It is a <fieldset> with a <legend>, NOT a floating label and NOT a <label>
   above an <input>. The legend sits in the border, painted over it with the
   surface colour. Two consequences worth knowing before you change it:

     - the legend's background must match the surface the field sits on. On a
       card that is --hk-card. A field dropped onto --hk-background will show
       a coloured notch, which is the usual symptom of this being copied to a
       new context without thought.
     - the focus ring is an inset box-shadow plus an outer ring, because a real
       border would move the legend.

   Accessibility: fieldset/legend groups controls, so the legend alone does not
   label the input for a screen reader. The input gets an explicit aria-label
   from the same string. */

export interface FieldProps extends Omit<ComponentProps<'fieldset'>, 'children'> {
  label: string
  required?: boolean
  hint?: string
  error?: string
  disabled?: boolean
  /** Surface the field sits on; drives the legend's background. */
  surface?: 'card' | 'background'
  children: (props: { id: string; 'aria-label': string; 'aria-invalid'?: boolean }) => ReactNode
}

export function Field({
  label, required, hint, error, disabled, surface = 'card', className, children, ...rest
}: FieldProps) {
  const id = useId()
  return (
    <div className="min-w-0 w-full">
      <fieldset
        disabled={disabled}
        className={cn(
          'w-full min-w-0 m-0 border-0 rounded-hk bg-transparent px-3 pt-1 pb-2 transition-shadow',
          'shadow-[inset_0_0_0_1px_rgb(0_0_0/0.1)] dark:shadow-[inset_0_0_0_1px_var(--hk-input-border)]',
          'focus-within:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--hk-ring)_85%,transparent),0_0_0_3px_color-mix(in_oklab,var(--hk-ring)_25%,transparent)]',
          disabled && 'bg-muted opacity-90',
          error && 'shadow-[inset_0_0_0_1px_var(--hk-destructive)]',
          className,
        )}
        {...rest}
      >
        <legend
          className={cn(
            'px-1 mb-0.5 text-sm font-medium text-[#374151] dark:text-[#cbd5e1]',
            surface === 'card' ? 'bg-card' : 'bg-background',
          )}
        >
          {label}
          {required && <span className="text-destructive ms-1" aria-hidden>*</span>}
        </legend>
        {children({ id, 'aria-label': label, ...(error ? { 'aria-invalid': true } : {}) })}
      </fieldset>
      {(hint || error) && (
        <p className={cn('mt-1 px-1 text-xs', error ? 'text-destructive' : 'text-muted-fg')}>
          {error ?? hint}
        </p>
      )}
    </div>
  )
}

/** The bare control that goes inside a Field. Transparent — the fieldset draws
 *  the box. */
export function FieldInput({ className, ...props }: ComponentProps<'input'>) {
  return (
    <input
      className={cn(
        'w-full bg-transparent border-0 outline-none h-6 leading-none p-0',
        'text-sm text-foreground font-sans placeholder:text-muted-fg',
        'disabled:cursor-not-allowed disabled:text-muted-fg',
        className,
      )}
      {...props}
    />
  )
}
